import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@wayflyer/auth-sdk";
import type { WayflyerSchemas } from "@wayflyer/api-types";
import { addError } from "@wayflyer/blackbox-fe";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

const isProduction = import.meta.env.PROD && !window.origin.includes("dev");
const shouldShowDefaultLauncher =
  window.location.pathname.startsWith("/funding/wayflyer-hosted-capital") ||
  window.location.pathname.startsWith("/verify-identity");

export const useIntercomLoaded = () => {
  const { data: appConfig } = useCustomerAppConfig();
  const customerUuid = appConfig?.customerUuid;
  const customerName = appConfig?.customerName;

  const { data: isIntercomLoaded } = useQuery({
    queryKey: ["intercom-chat-widget", customerUuid],
    queryFn: async () => {
      let intercomData;

      try {
        const { data } = await axiosClient.get<WayflyerSchemas.IntercomConfigSchema>("/api/wayflyer/intercom/config");
        intercomData = data;
      } catch {
        intercomData = {
          app_id: "vrn7wq9n",
          api_base: "https://api-iam.eu.intercom.io",
        };
      }

      let defaultFn;
      try {
        const Intercom = await import("@intercom/messenger-js-sdk");
        // pnpm v10 resolves this differently to v11
        // @ts-expect-error ts(2339)
        defaultFn = typeof Intercom.default === "function" ? Intercom.default : Intercom.default.default;
      } catch (error) {
        addError("Failed to import Intercom", error);
        return false;
      }

      try {
        if (customerUuid) {
          defaultFn({
            app_id: intercomData.app_id,
            api_base: intercomData.api_base,
            region: "eu",
            intercom_user_jwt: intercomData.jwt_token,
            company_id: customerUuid,
            company: {
              id: customerUuid,
              company_id: customerUuid,
              name: customerName ?? undefined,
            },
            hide_default_launcher: !shouldShowDefaultLauncher && isProduction,
          });
        } else {
          defaultFn({
            app_id: intercomData.app_id,
            api_base: intercomData.api_base,
            region: "eu",
            intercom_user_jwt: intercomData.jwt_token,
            hide_default_launcher: !shouldShowDefaultLauncher && isProduction,
          });
        }
        return true;
      } catch (error) {
        addError("Failed to initialize Intercom", error);
        return false;
      }
    },
    enabled: isProduction,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return isIntercomLoaded;
};
